import {
  Building2,
  FileText,
  Gavel,
  Monitor,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import {
  complexLabel,
  districtLabel,
  emptyOfficerForm,
  generateTemporaryPassword,
  highCourtLabel,
  toOfficerForm,
} from "./helpers";
import { validateOfficerForm } from "./validation";
import type {
  CourtOfficer,
  CourtOfficerErrors,
  CourtOfficerFormState,
  CourtOfficerRole,
  HighCourtRef,
  DistrictRef,
  ComplexRef,
} from "./types";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import useSWR from "swr";

interface CourtOfficerModalProps {
  isOpen: boolean;
  officer: CourtOfficer | null;
  highCourts: HighCourtRef[];
  districts: DistrictRef[];
  isSubmitting: boolean;
  initialCourtType?: CourtOfficerFormState["courtType"];
  onClose: () => void;
  onSubmit: (form: CourtOfficerFormState) => Promise<void>;
}

interface ComplexesResponse {
  data?: ComplexRef[];
}

const roleOptions: Array<{
  value: CourtOfficerRole;
  title: string;
  description: string;
  icon: typeof Gavel;
  color: string;
  bg: string;
}> = [
  {
    value: "Judge",
    title: "Judge",
    description: "High Court bench",
    icon: Gavel,
    color: "#2563EB",
    bg: "#EEF4FF",
  },
  {
    value: "Reader",
    title: "Reader",
    description: "Assigned to a courtroom",
    icon: FileText,
    color: "#16A34A",
    bg: "#ECFDF3",
  },
  {
    value: "Controller",
    title: "Controller",
    description: "Manages multiple courtrooms",
    icon: Monitor,
    color: "#9333EA",
    bg: "#F3E8FF",
  },
];

function fieldClass(error?: string) {
  return `h-9 w-full rounded-[8px] border bg-white px-3 text-sm text-slate-900 outline-none transition focus:ring-2 ${
    error
      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
      : "border-slate-200 focus:border-blue-400 focus:ring-blue-100"
  }`;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs font-medium text-red-600">{message}</p>;
}

export default function CourtOfficerModal({
  isOpen,
  officer,
  highCourts,
  districts,
  isSubmitting,
  initialCourtType = "high_courts",
  onClose,
  onSubmit,
}: CourtOfficerModalProps) {
  const [form, setForm] = useState<CourtOfficerFormState>(emptyOfficerForm);
  const [errors, setErrors] = useState<CourtOfficerErrors>({});
  const [initialForm, setInitialForm] =
    useState<CourtOfficerFormState>(emptyOfficerForm);
  const isEditing = Boolean(officer);
  const [courtroomSearch, setCourtroomSearch] = useState("");
  useEffect(() => {
    if (!isOpen) return;

    setErrors({});

    const initialData = officer
      ? toOfficerForm(officer)
      : {
          ...emptyOfficerForm,
          courtType: initialCourtType,
          password: generateTemporaryPassword(),
        };

    setForm(initialData);
    setInitialForm(initialData);
  }, [initialCourtType, isOpen, officer]);
  const hasChanges = useMemo(() => {
    if (!isEditing) return true;

    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm, isEditing]);
  const { data: complexData } = useSWR<ComplexesResponse>(
    form.courtType === "district_courts" && form.districtId
      ? `/api/court/get-complexes/${form.districtId}?page=1&limit=100`
      : null,
  );
  const { data: courtRoomData } = useSWR(
    form.courtType === "district_courts" && form.complexId
      ? `/api/court/get-courtrooms/${form.complexId}?page=1&limit=100`
      : null,
  );
  const { data: benchData } = useSWR(
    form.courtType === "high_courts" && form.highCourtId
      ? `/api/court/get-benches/${form.highCourtId}?page=1&limit=100`
      : null,
  );
  const { data: benchRoomData } = useSWR(
    form.courtType === "high_courts" && form.benchId
      ? `/api/court/get-bench-rooms/${form.benchId}?page=1&limit=100`
      : null,
  );
  const benches = benchData?.data || [];

  const benchRoomsData = benchRoomData?.data || [];
  const courtRoomsData = courtRoomData?.data || [];

  useEffect(() => {
    if (form.role === "Controller" && benches.length === 1 && !form.benchId) {
      setForm((prev) => ({
        ...prev,
        benchId: benches[0]._id,
      }));
    }
  }, [benches, form.benchId]);
  useEffect(() => {
    if (
      form.role === "Controller" &&
      form.courtType === "high_courts" &&
      form.benchId
    ) {
      update("courtrooms", []);
    }
  }, [form.benchId]);
  const states = useMemo(
    () =>
      Array.from(
        new Set(
          districts.map((district) => district.stateName).filter(Boolean),
        ),
      ).sort((a, b) => String(a).localeCompare(String(b))),
    [districts],
  );

  const filteredDistricts = useMemo(
    () =>
      districts
        .filter(
          (district) =>
            !form.stateName || district.stateName === form.stateName,
        )
        .sort((a, b) =>
          String(a.districtName || "").localeCompare(
            String(b.districtName || ""),
          ),
        ),
    [districts, form.stateName],
  );

  const complexes = useMemo(
    () =>
      (complexData?.data || []).sort((a, b) =>
        String(a.complexName || "").localeCompare(String(b.complexName || "")),
      ),
    [complexData?.data],
  );
  const filteredCourtrooms = useMemo(() => {
    const rooms =
      form.courtType === "high_courts" ? benchRoomsData : courtRoomsData;

    return rooms.filter((room: any) =>
      `${room.courtRoom || room.courtRoomName || ""}`
        .toLowerCase()
        .includes(courtroomSearch.toLowerCase()),
    );
  }, [form.courtType, benchRoomsData, courtRoomsData, courtroomSearch]);
  if (!isOpen) return null;

  const update = <K extends keyof CourtOfficerFormState>(
    key: K,
    value: CourtOfficerFormState[K],
  ) => {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "role") {
      next.assignmentId = "";
      next.highCourtId = "";
      next.benchId = "";
      next.courtrooms = [];
      next.stateName = "";
      next.districtId = "";
      next.complexId = "";
    }
      if (key === "courtType") {
        next.assignmentId = "";
        next.highCourtId = "";
        next.stateName = "";
        next.districtId = "";
        next.complexId = "";
      }
      if (key === "stateName") {
        next.districtId = "";
        next.complexId = "";
      }
      if (key === "districtId") {
        next.complexId = "";
      }
      return next;
    });
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateOfficerForm(form, { isEditing });
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      return;
    }
    await onSubmit(form);
  };

  const selectedRole = roleOptions.find((role) => role.value === form.role);
  const showDetails = Boolean(form.role);

  const submitButtonColor =
    form.courtType === "district_courts" && form.role === "Judge"
      ? "bg-[#f26422]"
      : form.role === "Reader"
        ? "bg-green-600"
        : form.role === "Controller"
          ? "bg-purple-600"
          : "bg-blue-600";

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-slate-950/35 px-3 py-8">
      <form
        onSubmit={submit}
        className="w-full max-w-[520px] overflow-hidden rounded-[16px] bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <h2 className="text-lg font-bold text-slate-950">
              {officer ? "Edit Officer" : "Create Officer"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {isEditing
                ? `${form.firstName} ${form.lastName} · ${form.role}`
                : "Select role, then fill in details"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-slate-200 text-slate-500 transition hover:bg-slate-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[calc(100vh-185px)] overflow-y-auto px-6 py-5">
          {!isEditing && (
            <div className="grid gap-3 sm:grid-cols-2">
              {roleOptions.map((role) => {
                const Icon = role.icon;
                const selected = form.role === role.value;
                const roleColor = role.color;
                const roleBg = role.bg;
                return (
                  <button
                    type="button"
                    key={role.value}
                    onClick={() => update("role", role.value)}
                    className={`flex min-h-[78px] items-center gap-3 rounded-[8px] border p-4 text-left transition ${
                      selected
                        ? ""
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    style={
                      selected
                        ? {
                            borderColor: roleColor,
                            background: `${roleColor}0F`,
                          }
                        : undefined
                    }
                  >
                    <span
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px]"
                      style={{ background: roleBg, color: roleColor }}
                    >
                      <Icon size={18} />
                    </span>
                    <span>
                      <span className="block text-sm font-bold text-slate-700">
                        {role.title}
                      </span>
                      <span className="mt-1 block text-xs text-slate-500">
                        {form.courtType === "district_courts" &&
                        role.value === "Judge"
                          ? "District Court courtroom"
                          : role.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {isEditing && (
            <div className="mb-5 space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Role
              </label>

              <div className="flex items-center justify-between rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-3">
                <span className="font-medium text-slate-700">{form.role}</span>

                <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">
                  Locked
                </span>
              </div>

              <p className="text-sm text-slate-400">
                To change role, deactivate this officer and create a new one.
              </p>
            </div>
          )}
          {!isEditing && <FieldError message={errors.role} />}

          {showDetails && (
            <>
              <div className="my-5 border-t border-slate-100" />

              <div className="grid gap-3 sm:grid-cols-2">
                <label>
                  <span className="mb-1 block text-xs font-bold text-slate-700">
                    First Name <span className="text-red-500">*</span>
                  </span>
                  <input
                    value={form.firstName}
                    onChange={(event) =>
                      update(
                        "firstName",
                        event.target.value.replace(/[^A-Za-z -]/g, ""),
                      )
                    }
                    className={fieldClass(errors.firstName)}
                    placeholder="First name"
                  />
                  <FieldError message={errors.firstName} />
                </label>

                <label>
                  <span className="mb-1 block text-xs font-bold text-slate-700">
                    Last Name <span className="text-red-500">*</span>
                  </span>
                  <input
                    value={form.lastName}
                    onChange={(event) =>
                      update(
                        "lastName",
                        event.target.value.replace(/[^A-Za-z -]/g, ""),
                      )
                    }
                    className={fieldClass(errors.lastName)}
                    placeholder="Last name"
                  />
                  <FieldError message={errors.lastName} />
                </label>

                <label>
                  <span className="mb-1 block text-xs font-bold text-slate-700">
                    Email <span className="text-red-500">*</span>
                  </span>
                  <input
                    value={form.email}
                    onChange={(event) => update("email", event.target.value)}
                    className={fieldClass(errors.email)}
                    placeholder="Email"
                  />
                  <FieldError message={errors.email} />
                </label>

                <label>
                  <span className="mb-1 block text-xs font-bold text-slate-700">
                    Phone<span className="text-red-500">*</span>
                  </span>
                  <input
                    value={form.phone}
                    maxLength={10}
                    inputMode="numeric"
                    onChange={(event) =>
                      update("phone", event.target.value.replace(/\D/g, ""))
                    }
                    className={fieldClass(errors.phone)}
                    placeholder="Phone"
                  />
                  <FieldError message={errors.phone} />
                </label>

                <label>
                  <span className="mb-1 block text-xs font-bold text-slate-700">
                    Username <span className="text-red-500">*</span>
                  </span>
                  <input
                    value={form.username}
                    maxLength={20}
                    onChange={(event) =>
                      update(
                        "username",
                        event.target.value.replace(/[^a-zA-Z0-9]/g, ""),
                      )
                    }
                    className={fieldClass(errors.username)}
                    placeholder="Username"
                  />
                  <FieldError message={errors.username} />
                </label>

                {!isEditing && (
                  <label>
                    <span className="mb-1 block text-xs font-bold text-slate-700">
                      Temporary Password <span className="text-red-500">*</span>
                    </span>
                    <div className="flex gap-2">
                      <input
                        value={form.password}
                        disabled
                        className={fieldClass(errors.password)}
                        placeholder="Temporary password"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          update("password", generateTemporaryPassword())
                        }
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-slate-200 text-blue-600 transition hover:bg-blue-50"
                        title="Generate password"
                      >
                        <RefreshCw size={15} />
                      </button>
                    </div>
                    <FieldError message={errors.password} />
                  </label>
                )}
              </div>

              <label className="mt-3 block">
                <span className="mb-1 block text-xs font-bold text-slate-700">
                  Status
                </span>
                <select
                  value={form.status}
                  onChange={(event) =>
                    update(
                      "status",
                      event.target.value as CourtOfficerFormState["status"],
                    )
                  }
                  className={fieldClass(errors.status)}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </label>

              {!isEditing &&
                form.courtType === "high_courts" &&
                form.role === "Judge" && (
                  <div className="mt-4 rounded-[8px] border border-blue-200 bg-blue-50 p-4">
                    <div className="mb-3 flex items-center gap-2 text-sm font-bold text-blue-700">
                      <Building2 size={14} />
                      High Court Assignment
                    </div>
                    <label>
                      <span className="mb-1 block text-xs font-bold text-slate-700">
                        High Court <span className="text-red-500">*</span>
                      </span>
                      <select
                        value={form.highCourtId}
                        onChange={(e) => {
                          update("highCourtId", e.target.value);
                          update("benchId", "");
                          update("courtrooms", []);
                        }}
                        className={fieldClass(errors.highCourtId)}
                      >
                        <option value="" disabled>
                          - Select High Court -
                        </option>
                        {[...highCourts]
                          .sort((a, b) =>
                            highCourtLabel(a).localeCompare(highCourtLabel(b)),
                          )
                          .map((court) => (
                            <option
                              key={court._id || court.id}
                              value={court._id || court.id}
                            >
                              {highCourtLabel(court)}
                            </option>
                          ))}
                      </select>
                      <FieldError message={errors.highCourtId} />
                    </label>
                    {form.highCourtId && (
                      <>
                        <label className="mt-3 block">
                          <span className="mb-1 block text-xs font-bold text-slate-700">
                            Bench <span className="text-red-500">*</span>
                          </span>

                          <select
                            value={form.benchId}
                            onChange={(e) => {
                              update("benchId", e.target.value);
                              update("courtrooms", []);
                            }}
                            className={fieldClass(errors.benchId)}
                            disabled={!form.highCourtId}
                          >
                            <option value="" disabled>
                              Select Bench
                            </option>

                            {benches.map((bench: any) => (
                              <option key={bench._id} value={bench._id}>
                                {bench.benchName}
                              </option>
                            ))}
                          </select>
                          <FieldError message={errors.benchId} />
                        </label>
                      </>
                    )}
                    <p className="mt-3 text-xs text-slate-500">
                      Specific courtroom is assigned from Court Settings after
                      creation.
                    </p>
                  </div>
                )}
              {!isEditing &&
                form.courtType === "district_courts" &&
                form.role === "Reader" && (
                  <div className="mt-4 rounded-[8px] border border-green-300 bg-green-50 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-bold text-green-700">
                        <FileText size={14} />
                        Assign Courtroom
                      </div>
                      <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-bold text-green-700">
                        Optional - can do later
                      </span>
                    </div>
                    <p className="mb-3 text-xs leading-5 text-slate-600">
                      Assign this Reader to a specific courtroom now, or skip
                      and assign from the Officers list later.
                    </p>
                    <div className="space-y-3">
                      <label>
                        <span className="mb-1 block text-xs font-semibold text-slate-700">
                          State
                        </span>

                        <select
                          value={form.stateName}
                          onChange={(event) =>
                            update("stateName", event.target.value)
                          }
                          className="h-9  w-full rounded-[8px] 
                          border border-green-200 bg-white px-3 text-sm
                           text-slate-900 outline-none transition focus:border-green-400 
                           focus:ring-2 focus:ring-green-100"
                        >
                          <option value="" disabled>
                            Select state
                          </option>

                          {states.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </label>
                      {form.stateName && (
                        <label>
                          <span className="mb-1 block text-xs font-semibold text-slate-700">
                            District Court
                          </span>

                          <select
                            value={form.districtId}
                            onChange={(event) =>
                              update("districtId", event.target.value)
                            }
                            className="h-9 w-full rounded-[8px] border border-green-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-green-400 focus:ring-2 focus:ring-green-100"
                          >
                            <option value="" disabled>
                              Select district court
                            </option>

                            {filteredDistricts.map((district) => (
                              <option
                                key={district._id || district.id}
                                value={district._id || district.id}
                              >
                                {districtLabel(district)}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}

                      {form.districtId && (
                        <label>
                          <span className="mb-1 block text-xs font-semibold text-slate-700">
                            Complex / Bench
                          </span>

                          <select
                            value={form.complexId}
                            onChange={(event) =>
                              update("complexId", event.target.value)
                            }
                            className="h-9 w-full rounded-[8px] border border-green-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-green-400 focus:ring-2 focus:ring-green-100"
                          >
                            <option value="" disabled>
                              Select complex
                            </option>

                            {complexes.map((complex) => (
                              <option
                                key={complex._id || complex.id}
                                value={complex._id || complex.id}
                              >
                                {complexLabel(complex)}
                              </option>
                            ))}
                          </select>
                          <FieldError message={errors.highCourtId} />
                        </label>
                      )}
                    </div>
                  </div>
                )}

              {!isEditing &&
                form.courtType === "district_courts" &&
                form.role === "Controller" && (
                  <div className="mt-4 rounded-[8px] border border-purple-300 bg-purple-50 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-bold text-purple-700">
                        <Monitor size={14} />
                        Assign Courtrooms
                      </div>
                      <span className="rounded-full bg-purple-100 px-2.5 py-1 text-xs font-bold text-purple-700">
                        Optional - can do later
                      </span>
                    </div>
                    <p className="mb-3 text-xs leading-5 text-slate-600">
                      Select a court, then a bench/district, and assign up to 10
                      courtrooms.
                    </p>
                    <div className="space-y-3">
                      {/* <label>
                        <span className="mb-1 block text-xs font-semibold text-slate-700">
                          State
                        </span>

                        <select
                          value={form.stateName}
                          onChange={(event) =>
                            update("stateName", event.target.value)
                          }
                          className="h-9 w-full rounded-[8px] border border-purple-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                        >
                          <option value="">Select state</option>

                          {states.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </label> */}
                      <label>
                        <span className="mb-1 block text-xs font-semibold text-slate-700">
                          Court
                        </span>
                        <select
                          value={form.districtId}
                          // disabled={!form.stateName}
                          onChange={(event) =>
                            update("districtId", event.target.value)
                          }
                          className="h-9 w-full rounded-[8px] border border-purple-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                        >
                          <option value="" disabled>
                            Select court
                          </option>
                          {filteredDistricts.map((district) => (
                            <option
                              key={district._id || district.id}
                              value={district._id || district.id}
                            >
                              {district.districtName}, {district.stateName}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label>
                        <span className="mb-1 block text-xs font-semibold text-slate-700">
                          Complex
                        </span>
                        <select
                          value={form.complexId}
                          onChange={(event) =>
                            update("complexId", event.target.value)
                          }
                          className="h-9 w-full rounded-[8px] border border-purple-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                          disabled={!form.districtId}
                        >
                          <option value="" disabled>
                            Select complex
                          </option>
                          {complexes.map((complex) => (
                            <option
                              key={complex._id || complex.id}
                              value={complex._id || complex.id}
                            >
                              {complexLabel(complex)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <FieldError message={errors.benchId} />
                    </div>
                  </div>
                )}
              {!isEditing &&
                form.courtType === "district_courts" &&
                form.role === "Judge" && (
                  <div className="mt-4 rounded-[8px] border border-blue-300 bg-blue-50 p-4">
                    <div className="mb-3 flex items-center gap-2 text-sm font-bold text-blue-700">
                      <Building2 size={14} />
                      District Court Assignment
                    </div>
                    <div className="grid gap-3 sm:grid-cols-1">
                      <label>
                        <span className="mb-1 block text-xs font-bold text-slate-700">
                          State <span className="text-red-500">*</span>
                        </span>
                        <select
                          value={form.stateName}
                          onChange={(event) =>
                            update("stateName", event.target.value)
                          }
                          className={fieldClass(errors.stateName)}
                          
                        >
                          <option value="" disabled>
                            Select state
                          </option>
                          {states.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                        <FieldError message={errors.stateName} />
                      </label>

                      {form.stateName && (
                        <label>
                          <span className="mb-1 block text-xs font-bold text-slate-700">
                            District
                          </span>

                          <select
                            value={form.districtId}
                            onChange={(event) =>
                              update("districtId", event.target.value)
                            }
                            className={fieldClass(errors.districtId)}
                          >
                            <option value="" disabled>
                              Select district
                            </option>

                            {filteredDistricts.map((district) => (
                              <option
                                key={district._id || district.id}
                                value={district._id || district.id}
                              >
                                {district.districtName}
                              </option>
                            ))}
                          </select>

                          <FieldError message={errors.districtId} />
                        </label>
                      )}
                    </div>
                    <p className="mt-3 text-xs text-slate-500">
                      Specific courtroom is assigned from Court Settings after
                      creation.
                    </p>
                  </div>
                )}
              {!isEditing &&
                form.courtType === "high_courts" &&
                form.role === "Reader" && (
                  <div className="mt-4 rounded-[8px] border border-green-300 bg-green-50 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-bold text-green-700">
                        <FileText size={14} />
                        Assign Courtroom
                      </div>

                      <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-bold text-green-700">
                        Optional - can do later
                      </span>
                    </div>

                    <p className="mb-3 text-xs leading-5 text-slate-600">
                      Assign this Reader to a specific courtroom now, or skip
                      and assign from the Officers list later.
                    </p>

                    <label>
                      <span className="mb-1 block text-xs font-semibold text-slate-700">
                        High Court
                      </span>

                      <select
                        value={form.highCourtId}
                        onChange={(event) => {
                          update("highCourtId", event.target.value);
                          update("benchId", "");
                          update("courtrooms", []);
                        }}
                        className="h-9 w-full rounded-[8px] border border-green-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-green-400 focus:ring-2 focus:ring-green-100"
                      >
                        <option value="" disabled>
                          — Select High Court —
                        </option>

                        {[...highCourts]
                          .sort((a, b) =>
                            highCourtLabel(a).localeCompare(highCourtLabel(b)),
                          )
                          .map((court) => (
                            <option
                              key={court._id || court.id}
                              value={court._id || court.id}
                            >
                              {court.highCourtName}
                            </option>
                          ))}
                      </select>
                    </label>
                    {form.highCourtId && (
                      <label>
                        <span className="mb-1 mt-3 block text-xs font-semibold text-slate-700">
                          Bench
                        </span>

                        <select
                          value={form.benchId}
                          onChange={(event) => {
                            update("benchId", event.target.value);
                            update("courtrooms", []);
                          }}
                          className="h-9 w-full rounded-[8px] border border-green-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-green-400 focus:ring-2 focus:ring-green-100"
                        >
                          <option value="" disabled>Select Bench</option>

                          {benches.map((bench: any) => (
                            <option key={bench._id} value={bench._id}>
                              {bench.benchName}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                )}
              {!isEditing &&
                form.courtType === "high_courts" &&
                form.role === "Controller" && (
                  <div className="mt-4 rounded-[8px] border border-purple-300 bg-purple-50 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-bold text-purple-700">
                        <Monitor size={14} />
                        Assign Courtrooms
                      </div>

                      <span className="rounded-full bg-purple-100 px-2.5 py-1 text-xs font-bold text-purple-700">
                        Optional - can do later
                      </span>
                    </div>

                    <p className="mb-3 text-xs leading-5 text-slate-600">
                      Select a High Court, then Bench.
                    </p>

                    <label>
                      <span className="mb-1 block text-xs font-semibold text-slate-700">
                        High Court
                      </span>

                      <select
                        value={form.highCourtId}
                        onChange={(event) => {
                          update("highCourtId", event.target.value);
                          update("benchId", "");
                          update("courtrooms", []);
                        }}
                        className="h-9 w-full rounded-[8px] border border-purple-200 bg-white px-3 text-sm"
                      >
                        <option value="" disabled>
                          Select High Court
                        </option>

                        {[...highCourts]
                          .sort((a, b) =>
                            highCourtLabel(a).localeCompare(highCourtLabel(b)),
                          )
                          .map((court) => (
                            <option
                              key={court._id || court.id}
                              value={court._id || court.id}
                            >
                              {court.highCourtName}
                            </option>
                          ))}
                      </select>
                    </label>

                    {form.highCourtId && (
                      <label className="mt-3 block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700">
                          Bench
                        </span>

                        <select
                          value={form.benchId}
                          onChange={(event) => {
                            update("benchId", event.target.value);
                            update("courtrooms", []);
                          }}
                          className="h-9 w-full rounded-[8px] border border-purple-200 bg-white px-3 text-sm"
                        >
                          <option value="" disabled>
                            Select Bench
                          </option>

                          {benches.map((bench: any) => (
                            <option key={bench._id} value={bench._id}>
                              {bench.benchName}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {form.benchId && (
                      <>
                        <div className="mt-4 flex items-center justify-between">
                          <label className="text-sm font-semibold text-slate-700">
                            Courtrooms
                          </label>

                          <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-bold text-purple-700">
                            {form.courtrooms.length} / 10
                          </span>
                        </div>

                        <div className="mt-2">
                          {form.courtrooms.length > 0 && (
                            <div className="mb-3 flex flex-wrap gap-2">
                              {form.courtrooms.map((id) => {
                                const room = benchRoomsData.find(
                                  (item: any) => item._id === id,
                                );

                                if (!room) return null;

                                return (
                                  <span
                                    key={id}
                                    className="inline-flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-700"
                                  >
                                    <span className="text-sm">
                                      {room.benchName
                                        ? `${room.benchName} (${room.courtRoom})`
                                        : room.courtRoomName}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        update(
                                          "courtrooms",
                                          form.courtrooms.filter(
                                            (value) => value !== id,
                                          ),
                                        )
                                      }
                                      className="text-purple-500 hover:text-purple-700"
                                    >
                                      <X size={14} />
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          <div className="relative">
                            <Search
                              size={16}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />

                            <input
                              value={courtroomSearch}
                              onChange={(e) =>
                                setCourtroomSearch(e.target.value)
                              }
                              placeholder="Search courtrooms..."
                              className="h-10 w-full rounded-lg border border-purple-200 pl-9 pr-3 text-sm outline-none"
                            />
                          </div>

                          <div className="mt-3 max-h-44 overflow-y-auto rounded-lg border border-purple-200 bg-white">
                            {filteredCourtrooms.map((room: any) => (
                              <label
                                key={room._id}
                                className="flex cursor-pointer items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
                              >
                                <input
                                  type="checkbox"
                                  checked={form.courtrooms.includes(room._id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (form.courtrooms.length >= 10) return;

                                      update("courtrooms", [
                                        ...form.courtrooms,
                                        room._id,
                                      ]);
                                    } else {
                                      update(
                                        "courtrooms",
                                        form.courtrooms.filter(
                                          (id) => id !== room._id,
                                        ),
                                      );
                                    }
                                  }}
                                />

                                <span className="text-sm">
                                  <span className="text-sm">
                                    {room.benchName
                                      ? `${room.benchName} (${room.courtRoom})`
                                      : room.courtRoomName}
                                  </span>
                                </span>
                              </label>
                            ))}
                            {filteredCourtrooms.length === 0 && (
                              <div className="px-4 py-3 text-sm text-slate-500">
                                No courtrooms found.
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
            </>
          )}
        </div>

        <div className="grid gap-3 border-t border-slate-100 bg-white px-6 py-5 sm:grid-cols-[1fr_2fr]">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-[8px] border border-slate-200 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSubmitting || !form.role || (isEditing && !hasChanges)}
            className={`h-11 rounded-[8px] ${submitButtonColor} text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:bg-slate-300`}
          >
            {isSubmitting
              ? "Saving..."
              : officer
                ? "Save Changes"
                : selectedRole
                  ? "Create Officer"
                  : "Create Officer"}
          </button>
        </div>
      </form>
    </div>
  );
}
